import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useNavigationType, useParams } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { AuthProvider, useAuth } from './lib/auth-context';
import { DancerSessionProvider, useDancerSession, useOptionalDancerSession } from './lib/dancer-session-context';
import { signOut as signOutFirebase } from './lib/auth';
import { useAppOnScreenHandshake } from './lib/bootHandshake';
import { useAlertTapRouting } from './lib/alertTapRouting';
import { ThemeProvider } from './lib/theme';
import { SeasonProvider } from './lib/season';
// Every route below loads through lazyRoute(), never bare lazy() - see its
// doc comment (hub/src/lib/lazyRoute.ts) for why a bare React.lazy() call
// here can crash into AppErrorBoundary on a stale post-deploy chunk instead
// of recovering. tests/lazy-route-contract.test.mjs pins this at zero bare
// lazy( route imports so a future paste-in cannot silently regress it.
import { lazyOptional, lazyRoute } from './lib/lazyRoute';
import {
  RouteWarmer,
  WARM_WATCH_MS,
  WARM_POLL_MS,
  WARM_QUIET_MS,
  inAppPath,
  linkedPaths,
  pageStillLoading,
  routeObjectsFrom,
  warmingAllowed,
  warmingBlockedForGood,
  type NavigatorWithConnection,
  type WarmContext,
} from './lib/routeWarm';
import OptionalFeatureBoundary from './components/OptionalFeatureBoundary';
import { markBoot } from './lib/bootTiming';
import {
  firstNameFrom,
  homeShellKindFor,
  readLastHomeShell,
  rememberHomeShell,
} from './lib/lastHomeShell';
import LastHomeShell from './components/LastHomeShell';
import Landing from './pages/Landing';
import InstallPrompt from './components/InstallPrompt';
import AppBadgeSync from './components/AppBadgeSync';
import FeedbackOutboxSync from './components/FeedbackOutboxSync';
// LAZY on purpose. Both of these reach lib/push.ts, which statically imports
// firebase/messaging, so importing them eagerly put messaging in the bundle
// every visitor downloads and parses BEFORE the first render. Lazy moves it
// out of that bundle; it does NOT stop the download. Both components still
// mount for every visitor who is not in a dancer-only session, signed out
// included, so their chunks (and messaging with them) are fetched as soon
// as the main view renders, alongside the page instead of ahead of it.
// Neither renders anything on a normal load (PushSync is a listener,
// PushOptInGate returns null unless it has to ask), so the null fallback
// below is a real no-op rather than a swapped-in placeholder.
// lazyOptional(), not lazyRoute(): a failed chunk here must cost this visit
// its notifications and nothing else, so it goes to the
// OptionalFeatureBoundary around each one instead of reloading the whole
// Hub and spending the stale-chunk cooldown (PR #1242 refute round). Never
// bare lazy(), which tests/lazy-route-contract.test.mjs forbids.
const PushSync = lazyOptional(() => import('./components/PushSync'));
const PushOptInGate = lazyOptional(() => import('./components/PushOptInGate'));
// The kids' top bar (Menu, Search, Home), for a dancer-only session only.
// Lazy so no grown-up loads it, and lazyOptional for the same reason as the
// two above: a chunk that fails to load costs her this visit's bar, never the
// Hub. Her home's own row of doors still works without it.
const DancerChrome = lazyOptional(() => import('./components/dancer/DancerChrome'));
// The app-wide "still sending" banner, and through it the upload manager and
// progress bar (speed plan 2026-10-19, P2: about 100 KB of upload source was
// on every visitor's startup path). Still mounted on every page, so its chunk
// arrives right after the first screen, well before anyone can start an
// upload, and an interrupted upload from last time is still named. Optional
// for the same reason as the three above: a failed chunk costs this visit
// the banner, never the Hub, and the composer that is sending still shows
// its own progress.
const StillSendingBanner = lazyOptional(() => import('./components/StillSendingBanner'));
import { showsDancerChrome } from './lib/dancerChromeGate';
import GlobalChrome from './components/GlobalChrome';
import RouteScrollReset, { ScrollMemoryTracker } from './components/RouteScrollReset';
import { installFragmentEntryStamp, memoryKeyFor } from './lib/routeScroll';
import PickupReturnTrip from './components/PickupReturnTrip';
import StudioLoading from './components/StudioLoading';
import { isDemoStudioEnabled } from './lib/demoStudio';
import { isDancerLoginUserDoc, useUserDoc } from './lib/users';
import { canRenderAdultOptimistically } from './lib/dancerSessionHint';
import { isCostumePickupPath } from './lib/returnTo';
import ShoutoutWallGate from './components/ShoutoutWallGate';

// Every plain #anchor tap makes a history entry with no identity of its own;
// this gives each one an id so the scroll memory can tell two separate visits to
// the same anchor apart (lib/routeScroll.ts). At MODULE scope on purpose: it has
// to be listening before the router mounts, so that it runs ahead of the router's
// own popstate handler and the router's first render of the entry sees the id.
if (typeof window !== 'undefined') installFragmentEntryStamp(window);

const HubCheck = lazyRoute(() => import('./pages/HubCheck'));
const NewEventDate = lazyRoute(() => import('./pages/NewEventDate'));
const AboutHub = lazyRoute(() => import('./pages/PublicInfo').then((module) => ({ default: module.AboutHub })));
const PrivacyPolicy = lazyRoute(() => import('./pages/PublicInfo').then((module) => ({ default: module.PrivacyPolicy })));
const TermsOfService = lazyRoute(() => import('./pages/PublicInfo').then((module) => ({ default: module.TermsOfService })));
const DesignPreview = lazyRoute(() => import('./pages/DesignPreview'));
const PalettePlayground = lazyRoute(() => import('./pages/PalettePlayground'));
const PrintRosters = lazyRoute(() => import('./pages/PrintRosters'));
const CostumeSizes = lazyRoute(() => import('./pages/CostumeSizes'));
const PrintSchedule = lazyRoute(() => import('./pages/PrintSchedule'));
const Account = lazyRoute(() => import('./pages/Account'));
const Notifications = lazyRoute(() => import('./pages/Notifications'));
const HowTo = lazyRoute(() => import('./pages/HowTo'));
const ManageUsers = lazyRoute(() => import('./pages/ManageUsers'));
const DancerHomePreview = lazyRoute(() => import('./pages/DancerHomePreview'));
const RoleBuilder = lazyRoute(() => import('./pages/RoleBuilder'));
const PagesBoardsSetup = lazyRoute(() => import('./pages/PagesBoardsSetup'));
const StaffDirectory = lazyRoute(() => import('./pages/StaffDirectory'));
const AuditLog = lazyRoute(() => import('./pages/AuditLog'));
const DemoStudio = lazyRoute(() => import('./pages/DemoStudio'));
const DemoView = lazyRoute(() => import('./pages/DemoView'));
const FeedbackReports = lazyRoute(() => import('./pages/FeedbackReports'));
const StudentAwards = lazyRoute(() => import('./pages/StudentAwards'));
// The Director's certificate printing page (Packet CX-9). Lazy for the same
// reason as StudentAwards: three sheets of art is a lot of bytes for a page
// opened a few times a month.
const AwardCertificates = lazyRoute(() => import('./pages/AwardCertificates'));
const ShoutoutWall = lazyRoute(() => import('./pages/ShoutoutWall'));
const ProductionTracker = lazyRoute(() => import('./pages/ProductionTracker'));
const ProductionReport = lazyRoute(() => import('./pages/ProductionReport'));
const StudioSettings = lazyRoute(() => import('./pages/StudioSettings'));
// The public website's wording and photos, and the nightly backup log. Both
// are Director-only pages; the gate is inside each page and, for anything
// that matters, in the Firestore rules.
const SiteContent = lazyRoute(() => import('./pages/SiteContent'));
const WebsiteAnalytics = lazyRoute(() => import('./pages/WebsiteAnalytics'));
const Backups = lazyRoute(() => import('./pages/Backups'));
const VideoShrinkTest = lazyRoute(() => import('./pages/VideoShrinkTest'));
const Automations = lazyRoute(() => import('./pages/Automations'));
const Policies = lazyRoute(() => import('./pages/Policies'));
// Bobbie, 2026-09-01: a dedicated FAQ page - Director/Manager add, edit and
// delete FAQ entries here (not just inside Studio Settings), and any staff
// role can submit a new FAQ request. See pages/Faq.tsx for the full ruling.
const Faq = lazyRoute(() => import('./pages/Faq'));
const BagGroups = lazyRoute(() => import('./pages/BagGroups'));
const BagGroupDetail = lazyRoute(() => import('./pages/BagGroupDetail'));
const CostumePickup = lazyRoute(() => import('./pages/CostumePickup'));
const CostumeLabels = lazyRoute(() => import('./pages/CostumeLabels'));
const CostumeStatus = lazyRoute(() => import('./pages/CostumeStatus'));
const CostumePickupReport = lazyRoute(() => import('./pages/CostumePickupReport'));
const CostumeReport = lazyRoute(() => import('./pages/CostumeReport'));
const CostumeGuide = lazyRoute(() => import('./pages/CostumeGuide'));
const CostumeTracker = lazyRoute(() => import('./pages/CostumeTracker'));
const Offerings = lazyRoute(() => import('./pages/Offerings'));
const ProductionGrid = lazyRoute(() => import('./pages/ProductionGrid'));
const ClassLevels = lazyRoute(() => import('./pages/ClassLevels'));
const Offering = lazyRoute(() => import('./pages/Offering'));
// The redesigned single class page: one page per class. It was a superset of
// the old teacher class workspace page, since retired - /teach/:offeringId
// now redirects here - so every teacher/staff card that used to open the
// workspace opens this instead. It is also a superset of the old family
// class feed, since retired the same way - /class/:offeringId/feed now
// redirects here too (ClassFeedRedirect above). Offering stays live for its
// own old deep links.
const ClassPage = lazyRoute(() => import('./pages/ClassPage'));
const Students = lazyRoute(() => import('./pages/Students'));
const StudentsImport = lazyRoute(() => import('./pages/StudentsImport'));
const StartDates = lazyRoute(() => import('./pages/StartDates'));
const ImportRoster = lazyRoute(() => import('./pages/ImportRoster'));
const ImportEnrollments = lazyRoute(() => import('./pages/ImportEnrollments'));
const StaffDancerView = lazyRoute(() => import('./pages/StaffDancerView'));
const Injuries = lazyRoute(() => import('./pages/Injuries'));
const PrintDancerSchedule = lazyRoute(() => import('./pages/PrintDancerSchedule'));
const Teach = lazyRoute(() => import('./pages/Teach'));
const MusicLibrary = lazyRoute(() => import('./pages/MusicLibrary'));
const SeasonPreview = lazyRoute(() => import('./pages/SeasonPreview'));
const PUBLIC_INFO_PATHS = new Set([
  '/about',
  '/privacy',
  '/privacy-policy',
  '/terms',
  '/terms-of-service',
]);

function isPublicInfoPath(pathname: string): boolean {
  // /s/:token, the comp share page (N14). Netlify serves it from its own
  // entry (share.html, shareMain.tsx), which loads no Firebase, Sentry or
  // service worker. This branch is only the fallback when the main app
  // answers that address (the dev server, a missing rewrite): no auth
  // providers mount, though this bundle has already loaded Firebase, and
  // main.tsx keeps Sentry and the service worker off.
  return PUBLIC_INFO_PATHS.has(pathname) || pathname.startsWith('/s/');
}

// Growth, the twice-a-year progress reports. Lazy like the rest of the big
// pages: the rubric plus four screens is a lot of bytes for a section that
// is only live twice a year.
const Growth = lazyRoute(() => import('./pages/Growth'));
// Assessment: a teacher's one-on-one look at one dancer (DECISIONS 103, 104)
// Lazy so a page most accounts never open costs the bundle nothing.
const Assessment = lazyRoute(() => import('./pages/Assessment'));
// The staff Practice Tracker view (roster, badge browser, challenges) -
// lazy for the same bundle-size reason as its Growth siblings; a director,
// manager, or teacher opens it far less often than the family/dancer pages.
const PracticeTrackerStaff = lazyRoute(() => import('./pages/PracticeTrackerStaff'));
const GrowthBatch = lazyRoute(() => import('./pages/GrowthBatch'));
const GrowthReport = lazyRoute(() => import('./pages/GrowthReport'));
const GrowthTeacherEval = lazyRoute(() =>
  import('./pages/GrowthEval').then((m) => ({ default: m.GrowthTeacherEval }))
);
const GrowthSelfEval = lazyRoute(() =>
  import('./pages/GrowthEval').then((m) => ({ default: m.GrowthSelfEval }))
);
// "See how it works": the staff-facing walkthrough door (Bobbie, 2026-08-14
// afternoon, DECISIONS.md). Static content, no Firestore reads, so it stays
// lazy purely for bundle size like its siblings above.
const GrowthHowItWorks = lazyRoute(() => import('./pages/GrowthHowItWorks'));
const Search = lazyRoute(() => import('./pages/Search'));
const Messages = lazyRoute(() => import('./pages/Messages'));
const Interns = lazyRoute(() => import('./pages/Interns'));
const AdminBoard = lazyRoute(() => import('./pages/AdminBoard'));
const ExecutiveTeachers = lazyRoute(() => import('./pages/ExecutiveTeachers'));
const CommandCenter = lazyRoute(() => import('./pages/CommandCenter'));
const BigLittleManager = lazyRoute(() => import('./pages/BigLittleManager'));
const JrStaffCheckin = lazyRoute(() => import('./pages/JrStaffCheckin'));
const JrStaffCommandCenter = lazyRoute(() => import('./pages/JrStaffCommandCenter'));
const TechniqueCommandCenter = lazyRoute(() => import('./pages/TechniqueCommandCenter'));
const BigLittleBoard = lazyRoute(() => import('./pages/BigLittleBoard'));
const CompBoard = lazyRoute(() => import('./pages/CompBoard'));
const CompetitionEvent = lazyRoute(() => import('./pages/CompetitionEvent'));
const DancerCompBoard = lazyRoute(() => import('./pages/DancerCompBoard'));
const NewMessage = lazyRoute(() => import('./pages/NewMessage'));
const ScheduledMessages = lazyRoute(() => import('./pages/ScheduledMessages'));
const Thread = lazyRoute(() => import('./pages/Thread'));
const Tasks = lazyRoute(() => import('./pages/Tasks'));
const NewTask = lazyRoute(() => import('./pages/NewTask'));
const Rooms = lazyRoute(() => import('./pages/Rooms'));
const Calendar = lazyRoute(() => import('./pages/Calendar'));
const DancerCalendar = lazyRoute(() => import('./pages/DancerCalendar'));
const NewReservation = lazyRoute(() => import('./pages/NewReservation'));
const CalendarImport = lazyRoute(() => import('./pages/CalendarImport'));
const AvailabilitySearch = lazyRoute(() => import('./pages/AvailabilitySearch'));
const FamilyRehearsalRequest = lazyRoute(() => import('./pages/FamilyRehearsalRequest'));
const RoomRequests = lazyRoute(() => import('./pages/RoomRequests'));
const ScheduleGrid = lazyRoute(() => import('./pages/ScheduleGrid'));
const Closures = lazyRoute(() => import('./pages/Closures'));
const OpenSoloTimes = lazyRoute(() => import('./pages/OpenSoloTimes'));
const ImportantDates = lazyRoute(() => import('./pages/ImportantDates'));
const Events = lazyRoute(() => import('./pages/Events'));
const EventDetail = lazyRoute(() => import('./pages/EventDetail'));
const MessageHistory = lazyRoute(() => import('./pages/MessageHistory'));
const Bulletins = lazyRoute(() => import('./pages/Bulletins'));
const Newsletters = lazyRoute(() => import('./pages/Newsletters'));
const Attendance = lazyRoute(() => import('./pages/Attendance'));
const AttendanceConcerns = lazyRoute(() => import('./pages/AttendanceConcerns'));
const AbsenceAhead = lazyRoute(() => import('./pages/AbsenceAhead'));
const AttendanceReport = lazyRoute(() => import('./pages/AttendanceReport'));
const CompReports = lazyRoute(() => import('./pages/CompReports'));
const EvalTimes = lazyRoute(() => import('./pages/EvalTimes'));
const SubRequests = lazyRoute(() => import('./pages/SubRequests'));
const Assistant = lazyRoute(() => import('./pages/Assistant'));
const Register = lazyRoute(() => import('./pages/Register'));
const Registrations = lazyRoute(() => import('./pages/Registrations'));
const Payroll = lazyRoute(() => import('./pages/Payroll'));
const StaffActivity = lazyRoute(() => import('./pages/StaffActivity'));
const UploadSpeedReport = lazyRoute(() => import('./pages/UploadSpeedReport'));
const RemovedPosts = lazyRoute(() => import('./pages/RemovedPosts'));
const FamilyDancers = lazyRoute(() => import('./pages/FamilyDancers'));
const FamilyClasses = lazyRoute(() => import('./pages/FamilyClasses'));
const FamilyCommandCenter = lazyRoute(() => import('./pages/FamilyCommandCenter'));
const FamilyDancerProfile = lazyRoute(() => import('./pages/FamilyDancerProfile'));
const FamilyCalendar = lazyRoute(() => import('./pages/FamilyCalendar'));
const CompChecklist = lazyRoute(() => import('./pages/CompChecklist'));
const FamilyVideos = lazyRoute(() => import('./pages/FamilyVideos'));
const FamilyMusic = lazyRoute(() => import('./pages/FamilyMusic'));
const ParentBoard = lazyRoute(() => import('./pages/ParentBoard'));
const JrStaffBoard = lazyRoute(() => import('./pages/JrStaffBoard'));
const CompParentsBoard = lazyRoute(() => import('./pages/CompParentsBoard'));
const RehearsalSchedules = lazyRoute(() => import('./pages/RehearsalSchedules'));
const RehearsalScheduleDetail = lazyRoute(() => import('./pages/RehearsalScheduleDetail'));
const RecitalBuilder = lazyRoute(() => import('./pages/RecitalBuilder'));
const Workshops = lazyRoute(() => import('./pages/Workshops'));
const WorkshopSignups = lazyRoute(() => import('./pages/WorkshopSignups'));
// The real printable flyer (task: "the workshop print flyer is not good").
// Its own route so nothing else on /workshops - other event cards, staff
// controls, nav - can ever be on the same printed page.
const SignupFlyer = lazyRoute(() => import('./pages/SignupFlyer'));
// Public, no-login "Request sign up" page (Bobbie: "we don't want people to
// need to be logged in to sign up"). Talks only to the publicSignup
// Netlify function, never Firestore directly - see SignupPublic.tsx.
const SignupPublic = lazyRoute(() => import('./pages/SignupPublic'));
// The no-login class page a rec family opens from a link pasted into
// Studio Pro. Reads only the publicClass Function, never Firestore, and
// sits OUTSIDE the authenticated routes on purpose - see PublicClass.tsx.
const PublicClass = lazyRoute(() => import('./pages/PublicClass'));
// The no-sign-in comp share page a family's link opens (audit row N14).
// Reads only the compSharePage Function; see PublicCompShare.tsx.
const PublicCompShare = lazyRoute(() => import('./pages/PublicCompShare'));
// Director-only list of those links, so pasting ~60 of them into Studio
// Pro is a checklist rather than a hunt for document ids.
const ClassLinks = lazyRoute(() => import('./pages/ClassLinks'));

// /hub-check + /design-preview are public diagnostic/showcase pages (no secrets).
// /signup/:eventId is also public and no-login - see SignupPublic.tsx.

function RouteLoading() {
  return <StudioLoading fullPage />;
}

function SigningOutScreen() {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-6)',
        background: 'var(--paper)',
        color: 'var(--ink-soft)',
        fontWeight: 700,
        textAlign: 'center',
      }}
    >
      Signing out safely...
    </div>
  );
}

function AccessCheckError({
  message,
  onRetry,
  allowFirebaseSignOut = false,
}: {
  message: string;
  onRetry: () => void;
  allowFirebaseSignOut?: boolean;
}) {
  return (
    <main
      style={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-5)',
        background: 'var(--bg)',
      }}
    >
      <section
        role="alert"
        style={{
          width: '100%',
          maxWidth: 440,
          padding: 'var(--space-5)',
          border: '1px solid var(--line)',
          borderRadius: 'var(--radius-lg)',
          background: 'var(--paper)',
          boxShadow: 'var(--shadow-sm)',
          textAlign: 'center',
        }}
      >
        <h1 style={{ margin: 0, color: 'var(--ink)', fontSize: 22 }}>We could not verify this account</h1>
        <p style={{ color: 'var(--ink-soft)', lineHeight: 1.5 }}>{message}</p>
        <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
          <button
            type="button"
            onClick={onRetry}
            style={{ minHeight: 44, fontWeight: 800, color: 'var(--ink)' }}
          >
            Try again
          </button>
          {allowFirebaseSignOut && (
            <button
              type="button"
              onClick={() => void signOutFirebase()}
              style={{ minHeight: 44, fontWeight: 800, color: 'var(--ink-soft)' }}
            >
              Sign out
            </button>
          )}
        </div>
      </section>
    </main>
  );
}

/**
 * The old Studio Board page folded into Messages (Bobbie: "Studio board
 * should be part of messages not a separate thing"). Old links keep
 * working: /board#discussion-{id} lands on that exact post's row in the
 * merged Messages list, which carries the matching #board-{id} anchor.
 */
function BoardRedirect() {
  const location = useLocation();
  const match = /^#discussion-(.+)$/.exec(location.hash);
  const to = match ? `/messages#board-${match[1]}` : '/messages';
  return <Navigate to={to} replace />;
}

/**
 * /teach/classes retired as a separate landing page (Bobbie: "essentially
 * this page just goes under the TODAY'S classes section and one less
 * landing page for teachers to keep track of"). The full class list now
 * lives on /teach itself, right under Today's classes. Every existing link
 * (Landing, ProductionTracker, NavDrawer, GlobalChrome breadcrumbs, Teach's
 * own footer) keeps working. The query string is
 * forwarded verbatim so the old ?type=comp deep link still opens /teach
 * filtered to comp dances, and the #all-classes marker tells /teach to
 * scroll to the folded list on arrival - a link labeled "My classes" lands
 * on the classes, not at the top of the teaching home.
 */
function TeachClassesRedirect() {
  const location = useLocation();
  return <Navigate to={`/teach${location.search}#all-classes`} replace />;
}

/**
 * /teach/:offeringId retired (the class workspace is now the class page's
 * job - staff already get the composer, roster, attendance link, notes,
 * comp look, music, video, and costume picks there). Every card that used
 * to send a teacher here now links straight to /class/:offeringId; this
 * redirect only catches a stray bookmark or old notification link, and
 * forwards the query string and hash verbatim so a saved ?tab=media link
 * still lands on the right tab.
 */
function TeachOfferingRedirect() {
  const { offeringId } = useParams<{ offeringId: string }>();
  const location = useLocation();
  return (
    <Navigate
      to={`/class/${encodeURIComponent(offeringId ?? '')}${location.search}${location.hash}`}
      replace
    />
  );
}

/**
 * /class/:offeringId/feed retired. ClassPage is the family-capability
 * superset (Mark absent with Undo, the same three-verb FamilyActions, the
 * composer, media with the family upload, costumes, roster, acks/replies/
 * reactions), per Bobbie's ruling that the parent class view should match
 * the teacher view (DECISIONS.md, "The parent class view should match the
 * teacher view"). Every card that used to send a family member to the old
 * class feed now links straight to /class/:offeringId; this redirect only
 * catches a stray bookmark or old notification link, and forwards the
 * query string and hash verbatim so a #class-post-<id> deep link still
 * lands on the right post (ClassPage's own deep-link effect opens Past
 * first if the post has already sunk there).
 */
function ClassFeedRedirect() {
  const { offeringId } = useParams<{ offeringId: string }>();
  const location = useLocation();
  return (
    <Navigate
      to={`/class/${encodeURIComponent(offeringId ?? '')}${location.search}${location.hash}`}
      replace
    />
  );
}

/**
 * Routes that must render with zero app chrome, so the Hub footer below
 * skips them by prefix/exact match rather than needing an opt-in flag on
 * every page. `/print/*` is the hard rule (Bobbie: printed rosters and
 * schedules cannot carry app chrome, same reasoning as GlobalChrome's own
 * `.no-print` treatment of the breadcrumb strip). `/hub-check` and
 * `/design-preview` are diagnostic/showcase pages, not real studio pages -
 * a link back to the studio's own FAQ has nothing to do with either one.
 */
function hidesHubFooter(pathname: string): boolean {
  if (pathname.startsWith('/print/')) return true;
  // A conversation is a viewport-owned screen with its own pinned composer.
  // Putting the normal-flow footer underneath it makes the document itself
  // scroll and can leave the composer floating through the message history.
  if (/^\/messages\/[^/]+$/.test(pathname)) return true;
  return pathname === '/hub-check' || pathname === '/design-preview' || pathname === '/season-preview';
}

/**
 * Quiet, permanent links to App Feedback and Policies & FAQ at the very bottom of every real
 * page (Bobbie, 2026-08-08: "Can we add a link to the FAQ's on all roles
 * and pages at the very bottom?"). One mount point here instead of editing
 * every page - see hidesHubFooter for the short exclusion list.
 *
 * Normal document flow, never fixed/sticky, so it can never sit on top of
 * a page's own bottom actions - it just becomes the last thing on the page,
 * same as any other footer. GlobalChrome already reserves FAB_CLEARANCE_H
 * of body padding below the last real content for the floating back/
 * jump-to-top buttons, so this gets the same clearance every page's own
 * last card already gets, for free.
 *
 * `<Link>`, not a plain `<a href>` (review P2): a raw anchor cold-reloads
 * the whole app on every tap, the exact bug class tests/internal-
 * navigation.test.mjs exists to kill - see its own new App.tsx check.
 */
function HubFooter({ pathname }: { pathname: string }) {
  if (hidesHubFooter(pathname)) return null;
  const linkStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 44,
    padding: '0 var(--space-3)',
    color: 'var(--ink-soft)',
    fontSize: 16,
    fontWeight: 800,
    textAlign: 'center',
    textDecoration: 'none',
  };
  return (
    <footer
      className="no-print"
      aria-label="Hub help links"
      style={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 'var(--space-2)',
        padding: 'var(--space-6) var(--space-4)',
      }}
    >
      <Link to="/feedback" style={linkStyle}>
        App Feedback
      </Link>
      <Link
        to="/policies"
        style={linkStyle}
      >
        Studio Policies &amp; FAQ
      </Link>
      <Link to="/about" style={linkStyle}>
        About the Hub
      </Link>
      <Link to="/privacy" style={linkStyle}>
        Privacy
      </Link>
      <Link to="/terms" style={linkStyle}>
        Terms
      </Link>
    </footer>
  );
}

/**
 * Page transitions (DESIGN.md §7): the active route fades + slides as you move
 * between screens, so navigation feels polished instead of snapping. A single
 * wrapper keyed on the path keeps it lightweight; reduced-motion users get an
 * instant swap.
 */
function AvailabilitySearchRoute() {
  const { authenticated: dancerAuthenticated, jrStaffDancer } = useDancerSession();
  const { realUserDoc, loading } = useUserDoc();
  // A PROMOTED Jr Staff dancer is staff here, so they get the staff search
  // rather than the family request form: the whole point of the parity is
  // that they reach what an adult Jr Staff reaches. An ordinary dancer
  // session still has no Firebase principal and still gets the family form.
  if (dancerAuthenticated && !jrStaffDancer) return <FamilyRehearsalRequest />;
  if (loading) return <RouteLoading />;
  if (realUserDoc?.status === 'active' && realUserDoc.role === 'family') {
    return <FamilyRehearsalRequest />;
  }
  return <AvailabilitySearch />;
}

/**
 * One route, two audiences. An ordinary dancer session has no Firebase auth
 * at all, so it can never render the adult board; it gets the portal-fed
 * read-only view. Same shape as AvailabilitySearchRoute above.
 *
 * A PROMOTED Jr Staff dancer is the exception both routes now share: they
 * DO have a Firebase principal, with the same role an adult Jr Staff
 * carries, so the adult board is exactly what the parity ruling says they
 * should see. Every boundary on it is still in the rules.
 */
function CompBoardRoute() {
  const { authenticated: dancerAuthenticated, jrStaffDancer } = useDancerSession();
  if (dancerAuthenticated && !jrStaffDancer) return <DancerCompBoard />;
  return <CompBoard />;
}

/**
 * One route, two audiences, same shape as CompBoardRoute above. An ordinary
 * dancer session has no Firebase auth, so the staff Calendar page (Firestore
 * reads through useAuth, useRooms, useOfferings) can never mount for it - it
 * gets the portal-fed DancerCalendar instead (Dancer View Round 2, part c,
 * Bobbie via Fiona's screenshots, 2026-08-16).
 *
 * A PROMOTED Jr Staff dancer is the exception both routes share: they carry a
 * real Firebase principal with the same role an adult Jr Staff has, so the
 * adult calendar is exactly what the parity ruling says they should see.
 */
function CalendarRoute() {
  const { authenticated: dancerAuthenticated, jrStaffDancer } = useDancerSession();
  if (dancerAuthenticated && !jrStaffDancer) return <DancerCalendar />;
  return <Calendar />;
}

/**
 * The scroll memory's key for the entry on screen (lib/routeScroll.ts
 * memoryKeyFor): location.key, except that the first entry of a visit and every
 * plain #anchor entry share react-router's 'default' and must not share a memory
 * entry.
 */
function memoryKeyForLocation(location: ReturnType<typeof useLocation>): string {
  return memoryKeyFor(
    location.key,
    typeof window === 'undefined' ? null : window.history.state,
    `${location.pathname}${location.search}${location.hash}`,
  );
}

// Which page each wrapper above ends up showing, so RouteWarmSync can fetch
// its code ahead of the tap (lib/routeWarm.ts). Same choice as each wrapper
// makes, from the same dancer-session test.
Object.assign(CalendarRoute, {
  preload: ({ dancer }: WarmContext) => (dancer ? DancerCalendar : Calendar).preload(),
});
Object.assign(CompBoardRoute, {
  preload: ({ dancer }: WarmContext) => (dancer ? DancerCompBoard : CompBoard).preload(),
});
Object.assign(TeachClassesRedirect, { preload: () => Teach.preload() });
Object.assign(TeachOfferingRedirect, { preload: () => ClassPage.preload() });
Object.assign(ClassFeedRedirect, { preload: () => ClassPage.preload() });
Object.assign(BoardRedirect, { preload: () => Messages.preload() });

/**
 * Every page address in the Hub. A function rather than inline in
 * AnimatedRouteViews so RouteWarmSync can read the same tree to know which
 * page a link opens (lib/routeWarm.ts), and the two can never disagree.
 */
function appRoutes() {
  return (
    <>
      <Route path="/" element={<Landing />} />
      <Route path="/about" element={<AboutHub />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/terms-of-service" element={<Navigate to="/terms" replace />} />
      <Route path="/hub-check" element={<HubCheck />} />
      <Route path="/design-preview" element={<DesignPreview />} />
      <Route path="/season-preview" element={<SeasonPreview />} />
      <Route path="/palette" element={<PalettePlayground />} />
      <Route path="/print/rosters" element={<PrintRosters />} />
      <Route path="/costume-sizes" element={<CostumeSizes />} />
      <Route path="/print/schedule" element={<PrintSchedule />} />
      <Route path="/account" element={<Account />} />
      <Route path="/notifications" element={<Notifications />} />
      <Route path="/how-to" element={<HowTo />} />
      <Route path="/search" element={<Search />} />
      {/* The comp page is a job of the Comp/Recital Tracker now (Bobbie,
          2026-08-06). The route stays alive as a redirect, never a 404:
          stored notifications, tasks and bookmarks still point at /comp,
          and replace:true keeps Back from bouncing off it. */}
      <Route path="/comp" element={<Navigate to="/production-tracker?tab=comp-dancers" replace />} />
      <Route path="/staff" element={<StaffDirectory />} />
      <Route path="/manage-users" element={<ManageUsers />} />
      {/* The Director's read-only look at one dancer's own home
          (packet J13). The page gates on the REAL role and the
          function behind it re-reads users/{uid} and refuses
          anyone else, so routing it costs nothing. */}
      <Route path="/preview/dancer/:studentId" element={<DancerHomePreview />} />
      {/* Same sign-in, verification and onboarding gates as Home.
          Only the Admin role selects AdminHome inside Landing. */}
      <Route path="/admin-view" element={<Landing adminView />} />
      <Route path="/role-builder" element={<RoleBuilder />} />
      <Route path="/pages-boards-setup" element={<PagesBoardsSetup />} />
      <Route path="/audit-log" element={<AuditLog />} />
      {isDemoStudioEnabled() && <Route path="/demo-studio" element={<DemoStudio />} />}
      {isDemoStudioEnabled() && <Route path="/demo/teacher" element={<DemoView view="company" />} />}
      {isDemoStudioEnabled() && <Route path="/demo/parent" element={<DemoView view="family" />} />}
      {isDemoStudioEnabled() && <Route path="/demo/admin" element={<DemoView view="foh" />} />}
      {isDemoStudioEnabled() && <Route path="/demo/jr" element={<DemoView view="ensemble" />} />}
      <Route path="/feedback" element={<FeedbackReports />} />
      <Route path="/student-awards" element={<StudentAwards />} />
      <Route path="/student-awards/certificates" element={<AwardCertificates />} />
      <Route path="/shoutout-wall" element={<ShoutoutWallGate><ShoutoutWall /></ShoutoutWallGate>} />
      <Route path="/production-tracker" element={<ProductionTracker />} />
      <Route path="/production-tracker/report" element={<ProductionReport />} />
      <Route path="/studio-settings" element={<StudioSettings />} />
      <Route path="/website" element={<SiteContent />} />
      <Route path="/website/analytics" element={<WebsiteAnalytics />} />
      <Route path="/backups" element={<Backups />} />
      <Route path="/video-shrink-test" element={<VideoShrinkTest />} />
      <Route path="/automations" element={<Automations />} />
      <Route path="/policies" element={<Policies />} />
      <Route path="/faq" element={<Faq />} />
      <Route path="/bag-groups" element={<BagGroups />} />
      <Route path="/bag-groups/:id" element={<BagGroupDetail />} />
      {/* /cp/<costume>/<dancer> is what every label printed from
          2026-09-25 encodes: one dancer's bag. The older class-wide
          /costume-pickup/<costume> stays for labels already printed and
          opens a chooser (pages/CostumePickup.tsx). */}
      <Route path="/cp/:costumeId/:studentId" element={<CostumePickup />} />
      <Route path="/costume-pickup/:costumeId" element={<CostumePickup />} />
      <Route path="/costume-labels" element={<CostumeLabels />} />
      <Route path="/costume-status" element={<CostumeStatus />} />
      {/* The pickup report (2026-09-26): what has gone home and what is
          still waiting, per dancer. Every active staff role opens it
          (canOpenCostumePickupReport). */}
      <Route path="/costume-pickups" element={<CostumePickupReport />} />
      {/* Kim's costume report and the costume how-to (Bobbie, 2026-11-02). */}
      <Route path="/costume-report" element={<CostumeReport />} />
      <Route path="/costume-guide" element={<CostumeGuide />} />
      {/* The Costume tracker, in every role's menu (Bobbie, 2026-11-03).
          A dancer session may stand here too: dancerPathAllowed below. */}
      <Route path="/costume-tracker" element={<CostumeTracker />} />
      <Route path="/offerings" element={<Offerings />} />
      <Route path="/production-grid" element={<ProductionGrid />} />
      <Route path="/class-levels" element={<ClassLevels />} />
      <Route path="/offerings/:id" element={<Offering />} />
      <Route path="/class/:offeringId/feed" element={<ClassFeedRedirect />} />
      <Route path="/class/:offeringId" element={<ClassPage />} />
      {/* /dancers is the name Bobbie actually uses; /students stays live
          too so old bookmarks and notification links keep working. */}
      <Route path="/students" element={<Students />} />
      <Route path="/dancers" element={<Students />} />
      <Route path="/students/import" element={<StudentsImport />} />
      <Route path="/dancers/import" element={<StudentsImport />} />
      <Route path="/dancers/import-enrollments" element={<ImportEnrollments />} />
      {/* N22: current dancers whose start no parent has confirmed. The
          startYear function is the permission (Director, Manager, FOH). */}
      <Route path="/dancers/start-dates" element={<StartDates />} />
      {/* Director-only comp-roster seed behind a button. The page gates
          on the active-Director doc; the importCompRoster function is the
          real permission (it re-proves active Director before any write). */}
      <Route path="/admin/import-roster" element={<ImportRoster />} />
      {/* Staff-facing dancer lookup (classes, attendance, progress,
          staff notes, injury log) - distinct from the Director/Admin
          roster page above and from /family/dancer/:id below. */}
      <Route path="/dancers/:id" element={<StaffDancerView />} />
      <Route path="/dancers/:id/print-schedule" element={<PrintDancerSchedule />} />
      <Route path="/injuries" element={<Injuries />} />
      <Route path="/register" element={<Register />} />
      <Route path="/registrations" element={<Registrations />} />
      <Route path="/payroll" element={<Payroll />} />
      {/* Packet SA, Director only. The page gates itself on
          canOpenStaffActivity and draws its own lock card; the
          staffActivityReport function re-checks the Director. */}
      <Route path="/staff-activity" element={<StaffActivity />} />
      {/* Speed plan P1, Director only. The page gates itself on
          canOpenUploadSpeedReport and draws its own lock card; the
          uploadRecords function re-checks the Director. */}
      <Route path="/upload-speed" element={<UploadSpeedReport />} />
      {/* Directors only (2026-10-01): find a hidden board post and put
          it back. The page gates itself on canRestoreBoardPosts and
          draws its own lock card; boardModeration re-checks the
          Director on every call. */}
      <Route path="/removed-posts" element={<RemovedPosts />} />
      <Route path="/teach" element={<Teach />} />
      <Route path="/solo-music" element={<MusicLibrary key="solo" mode="solo" />} />
      <Route path="/all-music" element={<MusicLibrary key="all" mode="all" />} />
      <Route path="/growth" element={<Growth />} />
      <Route path="/assessment" element={<Assessment />} />
      <Route path="/practice-tracker" element={<PracticeTrackerStaff />} />
      <Route path="/growth/how-it-works" element={<GrowthHowItWorks />} />
      <Route path="/growth/batch/:cycleId" element={<GrowthBatch />} />
      <Route path="/growth/eval/:cycleId/:studentId" element={<GrowthTeacherEval />} />
      <Route path="/growth/self/:cycleId/:studentId" element={<GrowthSelfEval />} />
      <Route path="/growth/report/:cycleId/:studentId" element={<GrowthReport />} />
      <Route path="/teach/classes" element={<TeachClassesRedirect />} />
      <Route path="/teach/:offeringId" element={<TeachOfferingRedirect />} />
      <Route path="/messages" element={<Messages />} />
      <Route path="/interns" element={<Interns />} />
      <Route path="/executive-board" element={<ExecutiveTeachers />} />
      <Route path="/admin-board" element={<AdminBoard />} />
      <Route path="/command-center" element={<CommandCenter />} />
      <Route path="/command-center/big-little" element={<BigLittleManager />} />
      <Route path="/command-center/jr-staff" element={<JrStaffCommandCenter />} />
      <Route path="/command-center/technique" element={<TechniqueCommandCenter />} />
      <Route path="/jr-staff-checkin" element={<JrStaffCheckin />} />
      <Route path="/big-little" element={<BigLittleBoard />} />
      <Route path="/comp-board" element={<CompBoardRoute />} />
      <Route path="/comp-board/events/:eventId" element={<CompetitionEvent />} />
      <Route path="/messages/history" element={<MessageHistory />} />
      <Route path="/messages/new" element={<NewMessage />} />
      <Route path="/messages/scheduled" element={<ScheduledMessages />} />
      <Route path="/messages/:id" element={<Thread />} />
      <Route path="/mentions" element={<Navigate to="/messages" replace />} />
      <Route path="/tasks" element={<Tasks />} />
      <Route path="/tasks/new" element={<NewTask />} />
      <Route path="/rooms" element={<Rooms />} />
      <Route path="/calendar" element={<CalendarRoute />} />
      <Route path="/calendar/events/new" element={<NewEventDate />} />
      <Route path="/calendar/new" element={<NewReservation />} />
      <Route path="/calendar/search" element={<AvailabilitySearchRoute />} />
      <Route path="/calendar/requests" element={<RoomRequests />} />
      <Route path="/calendar/grid" element={<ScheduleGrid />} />
      <Route path="/calendar/closures" element={<Closures />} />
      <Route path="/calendar/open-solos" element={<OpenSoloTimes />} />
      <Route path="/calendar/important-dates" element={<ImportantDates />} />
      <Route path="/events" element={<Events />} />
      <Route path="/events/:eventId" element={<EventDetail />} />
      <Route path="/calendar/parent-watch" element={<Navigate to="/calendar" replace />} />
      <Route path="/calendar/import" element={<CalendarImport />} />
      <Route path="/rehearsals" element={<RehearsalSchedules />} />
      <Route path="/rehearsals/:id" element={<RehearsalScheduleDetail />} />
      <Route path="/recital-builder" element={<RecitalBuilder />} />
      <Route path="/bulletins" element={<Bulletins />} />
      {/* Newsletters folded into Announcements (Bobbie: "newsletters and
          bulletins... can they be combined?"). The list lives on
          /bulletins now; /newsletters/:id still renders the full long
          read so old notification links and bookmarks keep landing. */}
      <Route path="/newsletters" element={<Navigate to="/bulletins" replace />} />
      <Route path="/newsletters/:id" element={<Newsletters />} />
      <Route path="/attendance" element={<Attendance />} />
      <Route path="/attendance/concerns" element={<AttendanceConcerns />} />
      <Route path="/absences/ahead" element={<AbsenceAhead />} />
      <Route path="/attendance/report" element={<AttendanceReport />} />
      <Route path="/comp-reports" element={<CompReports />} />
      <Route path="/eval-times" element={<EvalTimes />} />
      <Route path="/subs" element={<SubRequests />} />
      <Route path="/board" element={<BoardRedirect />} />
      <Route path="/assistant" element={<Assistant />} />
      <Route path="/family/command-center" element={<FamilyCommandCenter />} />
      <Route path="/family/dancers" element={<FamilyDancers />} />
      <Route path="/family/classes" element={<FamilyClasses />} />
      <Route path="/family/dancer/:id" element={<FamilyDancerProfile />} />
      <Route path="/family/calendar" element={<FamilyCalendar />} />
      {/* Top-level, not under /family/: the checklist is one short
          path a parent types on comp morning, and the CompetitionEvent
          door points here too. */}
      <Route path="/comp-checklist" element={<CompChecklist />} />
      <Route path="/family/videos" element={<FamilyVideos />} />
      <Route path="/family/music" element={<FamilyMusic />} />
      <Route path="/community" element={<ParentBoard />} />
      <Route path="/jr-board" element={<JrStaffBoard />} />
      {/* The comp parents board (packet R43, ruling 9b): its own room,
          its own feed, its own yearly member list. Every read and write
          goes through netlify/functions/compParentsBoard.mjs, so the
          page itself is safe to route for anybody - a non-member gets
          the honest "not on this board" screen, never studio data. */}
      <Route path="/comp-parents-board" element={<CompParentsBoard />} />
      <Route path="/workshops" element={<Workshops />} />
      <Route path="/workshops/:eventId/flyer" element={<SignupFlyer />} />
      <Route path="/workshop-signups" element={<WorkshopSignups />} />
      <Route path="/signup/:eventId" element={<SignupPublic />} />
      {/* Public, no-login class page. Short path because it gets
          pasted into Studio Pro and typed by families. */}
      <Route path="/c/:offeringId" element={<PublicClass />} />
      {/* The comp share page (N14): a family's no-sign-in link. */}
      <Route path="/s/:token" element={<PublicCompShare />} />
      <Route path="/class-links" element={<ClassLinks />} />
      <Route path="*" element={<Landing />} />
    </>
  );
}

/**
 * Fetches the code for the pages this page links to once it has settled, and
 * a page's code the moment a finger lands on its link (lib/routeWarm.ts).
 * Renders nothing. Lives beside the pathname-keyed views, so one warmer (and
 * its memory of what it already fetched) lasts the whole visit.
 */
function RouteWarmSync() {
  const location = useLocation();
  // The optional hook, never the throwing one: this also mounts on /about, /privacy
  // and /terms, which App renders with no DancerSessionProvider (#1465 refute).
  const dancerSession = useOptionalDancerSession();
  const dancerRef = useRef(false);
  dancerRef.current = !!dancerSession?.authenticated && !dancerSession.jrStaffDancer;
  const warmer = useMemo(
    () =>
      new RouteWarmer(
        routeObjectsFrom(appRoutes()),
        () => ({ dancer: dancerRef.current }),
        () => warmingAllowed(navigator as NavigatorWithConnection, document),
        () => warmingBlockedForGood(navigator as NavigatorWithConnection),
      ),
    [],
  );
  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Element | null;
      const anchor = target && typeof target.closest === 'function' ? target.closest('a[href]') : null;
      if (!anchor) return;
      const path = inAppPath(anchor.getAttribute('href'), window.location.origin);
      if (path) warmer.warmNow(path);
    };
    document.addEventListener('pointerdown', onPointerDown, { capture: true, passive: true });
    return () => document.removeEventListener('pointerdown', onPointerDown, { capture: true });
  }, [warmer]);
  useEffect(() => {
    // Links keep arriving while a page fills in (a home paints its saved
    // outline first, then its cards), so the page is watched until its links
    // and its loading lines have both held still for WARM_QUIET_MS, and
    // warmed again if more links turn up later, until WARM_WATCH_MS.
    const startedAt = Date.now();
    let quietSince = startedAt;
    let lastLinks = '';
    let warmedLinks = '';
    const timer = window.setInterval(() => {
      const now = Date.now();
      const paths = linkedPaths(document, window.location.origin);
      const links = paths.join(' ');
      if (links !== lastLinks || pageStillLoading(document)) {
        lastLinks = links;
        quietSince = now;
      }
      warmer.resume();
      if (now - startedAt >= WARM_WATCH_MS && !warmer.hasPending()) window.clearInterval(timer);
      if (now - quietSince < WARM_QUIET_MS || links === warmedLinks) return;
      warmedLinks = links;
      warmer.warmPaths(paths);
    }, WARM_POLL_MS);
    return () => {
      window.clearInterval(timer);
      warmer.cancel();
    };
  }, [warmer, location.pathname]);
  return null;
}

function AnimatedRoutes() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const memoryKey = memoryKeyForLocation(location);
  return (
    <>
      {/* Remembers where each page was scrolled when it was left, so a Back to
          it can put that back (lib/scrollRestore.ts). BEFORE the pathname-keyed
          views, so its layout effect runs ahead of RouteScrollReset's in every
          commit; here, in the one place that outlives the pages. */}
      <ScrollMemoryTracker
        locationKey={memoryKey}
        pathname={location.pathname}
        navigationType={navigationType}
      />
      <RouteWarmSync />
      <AnimatedRouteViews />
    </>
  );
}

function AnimatedRouteViews() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const memoryKey = memoryKeyForLocation(location);
  const reduce = useReducedMotion();
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={reduce ? false : { opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={reduce ? undefined : { opacity: 0, y: -8 }}
        transition={{ duration: 0.22, ease: [0.2, 0.8, 0.2, 1] }}
      >
        {/* Opening a page starts at the top, not where the last page was
            scrolled (Packet CX-4, Bobbie: "when you click on this card it
            opens to the bottom of the class page instead of the top").
            FIRST child of the keyed wrapper, so it mounts with the new page
            and its layout effect runs before the page's own effects. The
            whole rule is in lib/routeScroll.ts; the placement reasoning is
            in components/RouteScrollReset.tsx. The location goes in as props
            so the page still fading out is not reset early. */}
        <RouteScrollReset
          pathname={location.pathname}
          hash={location.hash}
          state={location.state}
          navigationType={navigationType}
          locationKey={memoryKey}
        />
        <Suspense fallback={<RouteLoading />}>
          <Routes location={location}>
            {appRoutes()}
          </Routes>
        </Suspense>
      </motion.div>
    </AnimatePresence>
  );
}

function HubContent() {
  // FIRST OPEN, piece 1 (packet J12). Five of the six boot points are taken
  // in this render body rather than in an effect, and deliberately: each
  // one records the moment the app KNOWS an answer, and an effect runs
  // after the commit and after paint, which would fold a frame of unrelated
  // delay into every number. markBoot() is write-once and does nothing at
  // all on a repeat call, so StrictMode's double render in development and
  // every later re-render leave the first time standing (lib/bootTiming.ts).
  markBoot('ldg:first-render');
  const {
    signingOut,
    user,
    loading: authLoading,
    restoreTimedOut: authRestoreTimedOut,
    restoreTimedOutReason: authRestoreTimedOutReason,
    retryRestore: retryAuthRestore,
  } = useAuth();
  if (!authLoading) markBoot('ldg:auth-answered');
  const {
    state: dancerSessionState,
    authenticated: dancerAuthenticated,
    jrStaffDancer,
    jrStaffPromotion,
    noDancerHint,
    error: dancerSessionError,
    retry: retryDancerSession,
  } = useDancerSession();
  // "Is this a CHILD-ONLY session?" - which is what dancerAuthenticated
  // alone used to mean and stopped meaning with Jr Staff parity. A promoted
  // Jr Staff dancer holds a dancer cookie AND a real staff principal, so the
  // route clamp and the suppressed adult chrome below must not apply to
  // them: clamping would bounce them off every staff route they were just
  // given, and suppressing the chrome would leave them with no nav to reach
  // one. Their HOME is still DancerHome (see Landing) - they gain the staff
  // app, they do not lose their own.
  const dancerOnly = dancerAuthenticated && !jrStaffDancer;
  if (dancerSessionState !== 'loading') markBoot('ldg:dancer-session-answered');
  const {
    userDoc,
    loading: userDocLoading,
    error: userDocError,
    reason: userDocReason,
  } = useUserDoc();
  // A signed-out visitor has no account doc to answer for, so this point is
  // "the doc question is settled", not "a doc arrived". readBootTimings()
  // reports a point that never happened as "not recorded" rather than as a
  // zero, which would read as "instant" and is a different claim.
  if (!userDocLoading) markBoot('ldg:user-doc-answered');
  // PACKET J12 ITEM 3: the last known home shell. Read ONCE at mount, never
  // again: it only matters while the checks below are still running, and
  // re-reading it mid-session would let a value written by another tab
  // change what is on screen. Kind and first name only; see
  // lib/lastHomeShell.ts for why that is the whole record and why it opens
  // no door.
  const [lastHomeShell] = useState(readLastHomeShell);
  const resolvedHomeKind = homeShellKindFor({
    dancerAuthenticated,
    signedIn: Boolean(user),
    role: userDoc?.role ?? null,
    status: userDoc?.status ?? null,
  });
  const bootResolved = dancerSessionState !== 'loading' && !(user && userDocLoading);
  const resolvedFirstName = firstNameFrom(user?.displayName);
  useEffect(() => {
    // Only ever from a RESOLVED boot. A kind guessed while the checks are
    // still running would teach this device the wrong shell for next time,
    // which is the one way a hint like this does real damage.
    if (!bootResolved || !resolvedHomeKind) return;
    rememberHomeShell({ kind: resolvedHomeKind, firstName: resolvedFirstName });
  }, [bootResolved, resolvedHomeKind, resolvedFirstName]);
  const location = useLocation();
  if (signingOut) return <SigningOutScreen />;
  // The sign-in restore passed the Hub's own ten second deadline with no
  // answer from Firebase (lib/auth-context.tsx). Nobody has been signed out
  // and the restore is still running: this only stops the boot from holding
  // a blank screen for the SDK's own 30 to 60 second timeout, and it clears
  // itself the moment an auth state arrives. Same screen the dancer cookie
  // check lands on for the same reason.
  if (authLoading && authRestoreTimedOut) {
    return (
      <AccessCheckError
        message={
          authRestoreTimedOutReason === 'redirect'
            ? 'We are still waiting on the sign-in service, and Try again will not make it answer any sooner. Give it a moment and the Hub will open as soon as it answers.'
            : 'The sign-in check took too long. Check your connection and try again.'
        }
        onRetry={retryAuthRestore}
      />
    );
  }
  // Do not mount any route until the HttpOnly dancer cookie has been checked
  // - UNLESS this device's last real check already confirmed no dancer
  // cookie is present, and there is already an adult Firebase session to
  // render into (packet R38 Fix 1, "the Hub's first open"). That fetch is
  // `cache: 'no-store'` and fires for every visitor, every load, because it
  // reads an HttpOnly cookie no client JS can see - the one truly
  // unavoidable round trip in the whole boot sequence, and it answers "no"
  // for the large majority of visits. The fetch itself is UNCHANGED and
  // still runs (see dancer-session-context.tsx): this only stops blocking
  // RENDER on its result for the one case where the answer is already
  // known to be probably-no. A signed-out visitor, a device with no hint
  // yet, or an actual dancer session (noDancerHint is cleared the instant
  // one is confirmed, by a fresh check or a real login) all still wait
  // here exactly as before - this can only ever skip the wait for an
  // adult, never show a dancer the adult shell.
  const skipDancerCheckWait = canRenderAdultOptimistically({ noDancerHint, hasFirebaseUser: Boolean(user) });
  if (dancerSessionState === 'loading' && !skipDancerCheckWait) {
    // The shell of the home this device landed on last time, with the
    // spinner inside it, instead of a spinner on an empty page. A device
    // with nothing remembered (a first-ever visit, a private window,
    // storage blocked, or anyone who has signed out since) gets exactly the
    // plain spinner it got before this packet.
    if (lastHomeShell) return <LastHomeShell shell={lastHomeShell} />;
    return <StudioLoading fullPage label="Checking your account..." />;
  }
  if (dancerSessionState === 'error') {
    return (
      <AccessCheckError
        message={dancerSessionError ?? 'The secure sign-in check did not finish.'}
        onRetry={retryDancerSession}
      />
    );
  }

  // Resolve the live user doc before mounting any adult route. A missing or
  // failed doc is not a signed-out state and must never fall through to the
  // 92-route adult surface.
  //
  // PACKET J12 ITEM 2, ANSWERED AND DELIBERATELY UNCHANGED. These two
  // "Checking your account..." spinners are sequential in what they RENDER,
  // and it reads like a boot that waits twice. It is not: the two CHECKS
  // behind them already overlap, and making them "parallel" would move
  // nothing. Proven four ways, and the test that keeps it true is
  // tests/first-open-boot.test.mjs:
  //   a. useDancerSession() and useUserDoc() are both called at the TOP of
  //      this component, above every early return, so React runs both of
  //      their effects in the SAME commit.
  //   b. useUserDoc's snapshot effect waits on [user, authLoading,
  //      jrStaffDancer] (lib/users.ts). Nothing about the dancer cookie.
  //   c. The dancer status effect waits on [firebaseLoading, firebaseUser,
  //      retryKey] (lib/dancer-session-context.tsx). Nothing about the doc.
  //   d. Measured in Chromium against the production build: the account doc
  //      answered at 171 ms and the dancer cookie check at 177 ms on the
  //      same load, which the doc could not do if it were waiting on it.
  // Both DO wait on Firebase auth first. That is a shared prefix, not one
  // check waiting on the other, and it is why ldg:auth-answered sits above
  // both of them on /hub-check's First open readout.
  if (user && userDocLoading) {
    if (lastHomeShell) return <LastHomeShell shell={lastHomeShell} />;
    return <StudioLoading fullPage label="Checking your account..." />;
  }

  if (user && userDocError) {
    return (
      <AccessCheckError
        message={userDocError}
        onRetry={() => window.location.reload()}
        allowFirebaseSignOut
      />
    );
  }

  if (user && !userDoc) {
    if (userDocReason === 'unverified-email') {
      if (location.pathname !== '/') return <Navigate to="/" replace />;
      return <AnimatedRoutes />;
    }
    return (
      <AccessCheckError
        message={userDocError ?? 'Your account details did not load, so the Hub stayed safely closed.'}
        onRetry={() => window.location.reload()}
        allowFirebaseSignOut
      />
    );
  }

  // Old Firebase-backed dancer credentials are intentionally retired. Even
  // if one remains signed in on a device, it cannot regain adult routes.
  //
  // isDancerLoginUserDoc, NOT a raw studentId check (fixed 2026-08-19: a
  // bare `userDoc?.studentId` here locked EVERY Jr Staff account carrying an
  // identity link out of the entire adult app, staff role and all, on a
  // false "moved to the secure dancer sign-in" message). Three other places
  // already agree studentId alone is not enough: isDancerLoginUserDoc itself
  // excludes every staff role (lib/users.ts), setDancerLink.mjs says "A
  // staff account with only studentId remains a legitimate Jr Staff link",
  // and shared/firestore.rules' isActiveStudioGroupMember carve-out reads
  // "Real staff can also be dancers, so their staff role keeps access." This
  // gate was the one place in the app that had not caught up. Reusing the
  // helper, not a second role list, keeps the two definitions from drifting
  // apart again.
  if (user && isDancerLoginUserDoc(userDoc)) {
    return (
      <AccessCheckError
        message="This dancer login has moved to the secure dancer sign-in. Sign out, then use the dancer username on the welcome screen."
        onRetry={() => window.location.reload()}
        allowFirebaseSignOut
      />
    );
  }

  const dancerPathAllowed = location.pathname === '/'
    || location.pathname === '/policies'
    || location.pathname === '/how-to'
    // The comp board is one board for the whole comp team, dancers included,
    // so the route has to mean the same thing in a dancer session as it does
    // in their grown-up's. CompBoardRoute renders the read-only dancer view
    // there; the adult page is never mounted for a dancer session.
    || location.pathname === '/comp-board'
    // NOT /comp-board/events/:eventId, and not an oversight - checked
    // 2026-08-31 while adding the dancer jump-to-sections nav. That page
    // (pages/CompetitionEvent.tsx) reads useAuth/useUserDoc and Firestore
    // membership directly, the same adult-only shape CompBoardRoute and
    // CalendarRoute exist to route AROUND for a dancer session (no Firebase
    // principal at all) rather than widen into. Nothing a dancer session can
    // tap reaches this route today: DancerCompBoardSection (rides on this
    // page and on /comp-board) renders posts only, with no per-event links,
    // and DancerEventPills deliberately opens the dancer home's own Events
    // door instead of countdownEventHref's adult route for exactly this reason
    // (see that component's own comment). Widening this line would not fix
    // a live dead end; it would mount a Firestore page with no principal to
    // read it, which resolves to an access-denied screen, not a working
    // event page. A real fix needs a portal-fed DancerCompetitionEvent twin,
    // the same shape as DancerCompBoard/DancerCalendar - out of scope here.
    // /events and /events/:eventId (2026-10-18) stay off this list for the
    // same reason: both read countdownEvents through Firebase. A dancer's
    // Events door on her home lists the portal's own copy of those dates,
    // and her pills open that door on the one event she tapped.
    || location.pathname === '/calendar/search'
    // Dancer View Round 2, part c (Bobbie via Fiona's screenshots,
    // 2026-08-16): a dancer session had no calendar route at all.
    // CalendarRoute renders DancerCalendar here, the same portal-fed page as
    // above; the staff Calendar page is never mounted for a dancer session.
    || location.pathname === '/calendar'
    // HER COSTUME TRACKER (Bobbie, 2026-11-03: "all roles have a costume
    // tracker for what they mange"). CostumeTracker reads her own costumes
    // through the dancer portal for a dancer session; it never mounts a
    // Firebase read without a principal.
    || location.pathname === '/costume-tracker'
    // A dancer who scans her own costume label (2026-09-25) used to be
    // bounced silently to her home screen. CostumePickup renders a read-only
    // card for a dancer session: "A grown-up or the front desk marks it
    // picked up." It reads nothing and writes nothing.
    || isCostumePickupPath(location.pathname);

  if (dancerOnly && !dancerPathAllowed) return <Navigate to="/" replace />;

  // ldg:home-paint USED TO BE TAKEN HERE, and it was wrong (Codex review of
  // PR #1099, P2). This line is the moment every gate cleared, which is a
  // real thing to know, but it is not the moment a home is on screen:
  // AnimatedRoutes has not committed yet and the lazy chunk behind whichever
  // home this person gets has not even been fetched, so the number left out
  // exactly the lazy-load and render delay the beacon exists to measure. The
  // mark now comes from components/HomePaintMark.tsx, rendered inside each
  // home's own Suspense boundary in Landing.tsx and fired two animation
  // frames after that commit, so it lands after the paint. Same name, same
  // position in the readout; only the moment it records has been corrected.

  return (
    <>
      <AnimatedRoutes />
      {/* Mounted here, above the dancerOnly split below, not inside
          GlobalChrome where it used to live - R24 review, MEDIUM 5:
          GlobalChrome is suppressed entirely for a dancer-only session
          (see the !dancerOnly guard a few lines down), but a dancer session
          uploads attachments through this exact same shared hook and needs
          the same "still sending" / failure-recovery visibility an adult
          session gets. */}
      <OptionalFeatureBoundary feature="StillSendingBanner">
        <Suspense fallback={null}>
          <StillSendingBanner reserveSpace={!hidesHubFooter(location.pathname)} />
        </Suspense>
      </OptionalFeatureBoundary>
      {/* Below the routed content, in normal document flow - see HubFooter's
          own doc comment. Read here rather than inside AnimatedRoutes so
          the 92-route Routes block above stays untouched. */}
      {!dancerOnly && <HubFooter pathname={location.pathname} />}
      {!dancerOnly && <AppBadgeSync />}
      {!dancerOnly && user && userDoc?.status === 'active' && <FeedbackOutboxSync uid={user.uid} />}
      {/* A parent who signed in from a costume pickup label goes straight
          back to that bag (lib/returnTo.ts). Renders nothing. */}
      {!dancerOnly && <PickupReturnTrip />}
      {/* Re-mints the FCM token on start and is the ONLY thing that can
          show a push while the app is on screen. See PushSync.tsx. */}
      {!dancerOnly && (
        <OptionalFeatureBoundary feature="PushSync">
          <Suspense fallback={null}>
            <PushSync />
          </Suspense>
        </OptionalFeatureBoundary>
      )}
      {/* The loud opt-in: every active person, any un-enrolled device,
          until they click yes (Bobbie, 2026-08-07). See PushOptInGate. */}
      {!dancerOnly && (
        <OptionalFeatureBoundary feature="PushOptInGate">
          <Suspense fallback={null}>
            <PushOptInGate />
          </Suspense>
        </OptionalFeatureBoundary>
      )}
      {!dancerOnly && <GlobalChrome />}
      {/* The dancer-only twin of the line above (Bobbie, 2026-10-13: "And a
          search bar for the whole app to find their class", "And a menu
          barb"). Everything it draws comes from what DancerHome already
          loaded, except her menu pins: it reads and saves those itself
          through the menuPins Function (lib/menuPinsDancer.ts). A promoted Jr Staff
          dancer is not dancerOnly and keeps the grown-up chrome above, and
          while her Jr Staff answer is still coming (jrStaffPromotion
          'pending') she gets neither bar, so a Jr Staff never sees the kids'
          bar flash up before the grown-up one replaces it. The rule is
          lib/dancerChromeGate.ts's showsDancerChrome, so the UI harness
          mounts the bar through the same one. */}
      {showsDancerChrome({ authenticated: dancerAuthenticated, jrStaffDancer, jrStaffPromotion }) && (
        <OptionalFeatureBoundary feature="DancerChrome">
          <Suspense fallback={null}>
            <DancerChrome />
          </Suspense>
        </OptionalFeatureBoundary>
      )}
      {!dancerOnly && <InstallPrompt />}
    </>
  );
}

function App() {
  const location = useLocation();
  const publicInfoPath = isPublicInfoPath(location.pathname);
  // Boot watchdog handshake (task #125), moved out of src/main.tsx by the
  // first opening audit, I-3. The flag that disarms index.html's 15 second
  // reload offer is now set from this commit, not from the line after
  // ReactDOM.render, so it means "the app is on screen" rather than "the
  // bundle parsed". The whole reasoning lives in lib/bootHandshake.ts.
  useAppOnScreenHandshake();
  // B30: a tapped phone alert routes this open window in-app instead of the
  // worker reloading it (lib/alertTapRouting.ts).
  useAlertTapRouting();

  return (
    <ThemeProvider>
      <SeasonProvider>
        {publicInfoPath ? (
          // Google reviewers and signed-out families must be able to read the
          // app identity, Privacy Policy, and Terms without mounting Firebase,
          // dancer-session, push, or private-account providers.
          <AnimatedRoutes />
        ) : (
          <AuthProvider>
            <DancerSessionProvider>
              <HubContent />
            </DancerSessionProvider>
          </AuthProvider>
        )}
      </SeasonProvider>
    </ThemeProvider>
  );
}

export default App;
